import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import { Toast } from "react-native-toast-notifications";
import { router, useLocalSearchParams } from "expo-router";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import customAxios from "../../utils/customAxios";
import i18n from "../../locales/i18n";
import WebSidebar from "./WebSidebar";

const WebAddDetteScreen: React.FC = () => {
  const { locale } = useSelector((state: RootState) => state.app);
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [phone, setPhone] = useState("");
  const [buttonSpinner, setButtonSpinner] = useState(false);
  const { total, factureId } = useLocalSearchParams();

  // Check if current language is RTL (Arabic)
  const isRTL = locale === "ar" || i18n.locale === "ar";

  const totalString = Array.isArray(total) ? total[0] : total;
  const factureIdString = Array.isArray(factureId) ? factureId[0] : factureId;
  const shouldShowTotal = totalString && parseFloat(totalString) > 0;
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, "/");

  useEffect(() => {
    if (totalString && parseFloat(totalString) > 0) {
      setAmount(totalString);
    }
  }, [totalString]);

  const handelAdd = async () => {
    if (!name || !amount || !phone) {
      Toast.show(i18n.t("fillall"), {
        type: "danger",
      });
      return;
    }

    if (!/^\d{10}$/.test(phone.replace(/[\s-]/g, ""))) {
      Toast.show(i18n.t("invalidPhoneNumber"), {
        type: "danger",
      });
      return;
    }

    setButtonSpinner(true);

    try {
      if (totalString) {
        await customAxios.post(
          "/debt/facture",
          {
            fullname: name,
            phone: phone,
            amount: amount,
            factureid: factureIdString,
          },
          { withCredentials: true }
        );
      } else {
        await customAxios.post(
          "/debt",
          {
            fullname: name,
            phone: phone,
            amount: amount,
          },
          { withCredentials: true }
        );
      }

      Toast.show(i18n.t("addsuccess"), {
        type: "success",
      });
      setName("");
      setPhone("");
      setAmount("");
      router.back();
    } catch (error: any) {
      if (error?.response?.status === 403) {
        const errorMessage = error?.response?.data?.error || "";
        if (
          typeof errorMessage === "string" &&
          errorMessage.toLowerCase().includes("free tier")
        ) {
          Toast.show(
            i18n.t("freeTierLimitExceeded") + " " + i18n.t("upgradeRequired"),
            {
              type: "danger",
              duration: 5000,
            }
          );
          setTimeout(() => router.push("/(drawer)/params/limits"), 2000);
          return;
        }
      }
      const errorMessage =
        error.response?.data?.message || error.message || i18n.t("addfaild");
      Toast.show(errorMessage, { type: "danger" });
    } finally {
      setButtonSpinner(false);
    }
  };

  return (
    <div
      key={`add-dette-${locale}`}
      className="flex flex-1 bg-[#F5F5F5] min-h-screen flex-row"
    >
      <WebSidebar />

      <View style={styles.mainContent}>
        <ScrollView
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
        >
          {/* Header */}
          <div
            className={`flex flex-row items-center justify-between ${isRTL ? "flex-row-reverse" : ""}`}
          >
            <Text style={styles.headerTitle}>
              {i18n.t("credit")} {"  >  "}{" "}
              {i18n.t("addnewdette")}
            </Text>
            <View style={styles.headerIconContainer}>
              <Text style={styles.headerIcon}>💰</Text>
            </View>
          </div>

          {/* Blue Line */}
          <View style={styles.blueLine} />

          {/* Form Card */}
          <View style={styles.formCard}>
            {/* Total from Sale Header (if applicable) */}
            {shouldShowTotal && (
              <View style={styles.totalHeader}>
                <View style={styles.totalRow}>
                  <Text style={styles.totalLabel}>
                    {i18n.t("total")}:
                  </Text>
                  <Text style={styles.totalValue}>
                    {totalString} {i18n.t("currency")}
                  </Text>
                </View>
                <View style={styles.totalRow}>
                  <Text style={styles.totalLabel}>
                    {i18n.t("calender")}
                  </Text>
                  <Text style={styles.totalValue}>{date}</Text>
                </View>
                <View style={styles.totalNote}>
                  <Text style={styles.totalNoteText}>
                    ✓ {i18n.t("amountFilledFromSale")}
                  </Text>
                </View>
              </View>
            )}

            {/* Form Fields */}
            <View style={styles.formFields}>
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>
                  {i18n.t("nameofdain")}
                </Text>
                <TextInput
                  style={styles.input}
                  placeholder={i18n.t("nameofdain")}
                  value={name}
                  onChangeText={setName}
                  placeholderTextColor="#999"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>
                  {i18n.t("phonenumber")}
                </Text>
                <TextInput
                  style={styles.input}
                  placeholder="0555123456"
                  value={phone}
                  onChangeText={setPhone}
                  keyboardType="phone-pad"
                  placeholderTextColor="#999"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>
                  {i18n.t("quantityofdette")}{" "}
                  {shouldShowTotal && "★"}
                </Text>
                <TextInput
                  style={[
                    styles.input,
                    shouldShowTotal && styles.inputHighlighted,
                  ]}
                  placeholder="0"
                  value={amount}
                  onChangeText={setAmount}
                  keyboardType="numeric"
                  editable={!shouldShowTotal}
                  placeholderTextColor="#999"
                />
              </View>
            </View>

            {/* Buttons */}
            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={() => router.back()}
              >
                <Text style={styles.cancelButtonText}>
                  {i18n.t("cancel")}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.submitButton}
                onPress={handelAdd}
                disabled={buttonSpinner}
              >
                {buttonSpinner ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <Text style={styles.submitButtonText}>
                    {i18n.t("confirm")}
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </ScrollView>
      </View>
    </div>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F5F5F5",
    minHeight: "100vh" as any,
  },
  containerRTL: {
    flexDirection: "row-reverse",
  } as any,
  mainContent: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
    padding: 24,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 16,
    marginBottom: 16,
  },
  headerTitle: {
    fontSize: 28,
    fontWeight: "bold",
    color: "#333",
  },
  headerIconContainer: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: "#FFF3E0",
    alignItems: "center",
    justifyContent: "center",
  },
  headerIcon: {
    fontSize: 32,
  },
  blueLine: {
    height: 4,
    backgroundColor: "#1379A2",
    borderRadius: 2,
    marginBottom: 24,
  },
  formCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    padding: 32,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
    maxWidth: 600,
  },
  totalHeader: {
    backgroundColor: "#E0F2FE",
    borderRadius: 12,
    padding: 16,
    marginBottom: 24,
    borderWidth: 2,
    borderColor: "#0EA5E9",
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  totalLabel: {
    fontSize: 18,
    fontWeight: "600",
    color: "#333",
  },
  totalValue: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#0EA5E9",
  },
  totalNote: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: "#0EA5E9",
  },
  totalNoteText: {
    fontSize: 16,
    color: "#0EA5E9",
    fontWeight: "600",
    textAlign: "center",
  },
  formFields: {
    gap: 20,
  },
  inputGroup: {
    marginBottom: 16,
  },
  inputLabel: {
    fontSize: 16,
    fontWeight: "500",
    color: "#666",
    marginBottom: 8,
    textAlign: "right",
  },
  input: {
    backgroundColor: "#F8F8F8",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E0E0E0",
    paddingVertical: 14,
    paddingHorizontal: 16,
    fontSize: 18,
    textAlign: "right",
  },
  inputHighlighted: {
    backgroundColor: "#E0F2FE",
    borderColor: "#0EA5E9",
    borderWidth: 2,
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "center",
    gap: 16,
    marginTop: 32,
  },
  cancelButton: {
    backgroundColor: "#E0E0E0",
    borderRadius: 8,
    paddingVertical: 14,
    paddingHorizontal: 48,
  },
  cancelButtonText: {
    color: "#666",
    fontSize: 18,
    fontWeight: "600",
  },
  submitButton: {
    backgroundColor: "#1379A2",
    borderRadius: 8,
    paddingVertical: 14,
    paddingHorizontal: 48,
  },
  submitButtonText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "600",
  },
});

export default WebAddDetteScreen;
